'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Image from 'next/image'

// Martwa strefa wokół kwadratu — bez niej 500×499 dostaje pełną szerokość kafla,
// a 499×500 zostaje w wąskim polu okładki. Skok nieproporcjonalny do różnicy pliku.
const LANDSCAPE_RATIO_THRESHOLD = 1.05

// Przy SSR useLayoutEffect nie istnieje. Na kliencie musi być warstwą layoutu:
// okładka z cache ma znać proporcje przed pierwszym paintem, inaczej kafel
// renderuje się jako pionowy i dopiero potem przeskakuje.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

// Kafle listy montują się dopiero po pobraniu wyników, więc `<img>` startuje zawsze jako
// niezaładowany — także wtedy, gdy plik leży w cache i będzie gotowy po kilkunastu ms.
// Szkielet czeka na ten próg, żeby wejście na listę nie migało szarym polem i fade-em.
const PENDING_SKELETON_DELAY_MS = 250

// Po zmianie `src` przeglądarka trzyma wymiary poprzedniego obrazu aż do dekodu
// nowego — bez `complete` proporcje bywają wzięte z pliku, którego już nie ma.
const resolveLandscapeRatio = (image) => {
  if (!image?.complete || !image.naturalWidth || !image.naturalHeight) {
    return null
  }

  return image.naturalWidth / image.naturalHeight >= LANDSCAPE_RATIO_THRESHOLD
    ? `${image.naturalWidth} / ${image.naturalHeight}`
    : null
}

/**
 * Okładka kafelka z rozróżnieniem dwóch stanów:
 * - brak okładki (`src` puste albo błąd pobrania) — trwałe szare pole,
 * - okładka w trakcie pobierania — szkielet, po załadowaniu obraz wchodzi fade-em.
 *
 * Bez tego rozróżnienia oba stany wyglądały identycznie i pusty rekord był brany za nieudane ładowanie.
 */
export default function ProductTileImage({ src, alt = '', width = 190, height = 278, priority = false }) {
  // Start bez szkieletu: markup z serwera pokazuje okładkę od razu, a stan ładowania
  // dokłada dopiero efekt na kliencie. Odwrotnie okładka z cache byłaby wygaszona
  // aż do hydracji — na zwykłym łączu to widoczne blade kafle tuż po wejściu na stronę.
  const [isPending, setIsPending] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [landscapeRatio, setLandscapeRatio] = useState(null)
  const imageRef = useRef(null)
  const renderedSrcRef = useRef(src)
  const pendingTimeoutRef = useRef(null)

  const clearPendingTimeout = () => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current)
      pendingTimeoutRef.current = null
    }
  }

  // Nowe `src` musi zdjąć błąd jeszcze przed renderem — inaczej kafel zostaje na gałęzi
  // placeholdera, `<Image>` się nie montuje, a efekt poniżej nie ma czego sprawdzić.
  if (renderedSrcRef.current !== src) {
    renderedSrcRef.current = src
    setHasError(false)
    setIsPending(false)
    setLandscapeRatio(null)
  }

  // Obraz z cache bywa gotowy zanim React podepnie onLoad — wtedy zdarzenie już nie przyjdzie.
  // Tak samo błąd sprzed hydracji: `complete` jest wtedy prawdą, ale `naturalWidth` zerem.
  useIsomorphicLayoutEffect(() => {
    const image = imageRef.current

    if (!image) {
      return undefined
    }

    if (image.complete) {
      setHasError(!image.naturalWidth)
      setIsPending(false)
      setLandscapeRatio(resolveLandscapeRatio(image))
      return undefined
    }

    pendingTimeoutRef.current = setTimeout(() => setIsPending(true), PENDING_SKELETON_DELAY_MS)

    return clearPendingTimeout
  }, [src])

  const handleLoad = ({ currentTarget }) => {
    clearPendingTimeout()
    setLandscapeRatio(resolveLandscapeRatio(currentTarget))
    setIsPending(false)
  }

  const handleError = () => {
    clearPendingTimeout()
    setHasError(true)
  }

  if (!src || hasError) {
    return (
      <div className="product-tile__image">
        <div className="product-tile__placeholder" />
      </div>
    )
  }

  return (
    <div
      className={`product-tile__image${landscapeRatio ? ' product-tile__image--landscape' : ''}`}
      style={landscapeRatio ? { '--product-tile-image-ratio': landscapeRatio } : undefined}
    >
      {isPending && <div className="product-tile__pending" aria-hidden />}
      <Image
        ref={imageRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        priority={priority}
        className={`product-tile__loading-image${isPending ? ' is-pending' : ''}`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  )
}
